// Eskom PARCC — finding display split: reviewer note vs procedure (R3 #2).
//
// New captures store the procedure on `Finding.criterion` and the reviewer's
// note on `text`. LEGACY rows (pre-R3 CES captures) composed the two into one
// blob — `criterion + '\n' + note` — where the criterion line starts with a
// multi-level procedure code ("1.2 ", "3.10. "). This module is the ONE place
// that recovers `{ procedure, note }` for display: the register page and the
// CSV/XLSX exports all call it, so the split logic never drifts.

/** The two display halves of a finding line. */
export interface FindingDisplay {
  /** The working-paper procedure ("1.2 Inspect …"); '' when none applies. */
  procedure: string;
  /** The reviewer's note — what the register's Finding column shows. */
  note: string;
}

/**
 * A procedure-coded line: multi-level numbering ("1.2", "3.10."), optionally
 * dot/paren-terminated, followed by text. Single-level numbering ("1. …") is
 * NOT matched — imported findings legitimately start that way.
 */
const PROCEDURE_LINE = /^\d+(?:\.\d+)+[.)]?\s/;

/**
 * Split a finding into its procedure and reviewer-note halves.
 *
 * Precedence: a stored `criterion` wins outright (text is the note verbatim).
 * Otherwise, a LEGACY blob whose first line is procedure-coded is split on the
 * first newline; a single procedure-coded line is procedure-only (status-only
 * legacy rows stored just the criterion); anything else is a plain note.
 */
export function splitFindingDisplay(f: {
  text: string;
  criterion?: string | null;
}): FindingDisplay {
  const criterion = (f.criterion ?? '').trim();
  const text = f.text ?? '';
  if (criterion !== '') return { procedure: criterion, note: text };

  const firstBreak = text.indexOf('\n');
  const firstLine = (firstBreak === -1 ? text : text.slice(0, firstBreak)).trim();
  if (!PROCEDURE_LINE.test(firstLine)) return { procedure: '', note: text };
  if (firstBreak === -1) return { procedure: firstLine, note: '' };
  return { procedure: firstLine, note: text.slice(firstBreak + 1).trim() };
}
